import { z } from 'zod';

/**
 * Зеркала enum из `packages/db/prisma/schema.prisma`.
 * Объявлены как const-объекты (как генерирует Prisma), чтобы web/admin не зависели от @prisma/client.
 * При изменении enum в схеме — менять здесь и в BLUEPRINT раздел 5.
 */

export const Role = {
  MEMBER: 'MEMBER',
  CAPTAIN: 'CAPTAIN',
  OPS: 'OPS',
  ADMIN: 'ADMIN',
  /** Сотрудник площадки: только портал площадки, вне иерархии MEMBER…ADMIN (Stage 14, ADR-27) */
  VENUE: 'VENUE',
} as const;
export type Role = (typeof Role)[keyof typeof Role];
export const RoleSchema = z.nativeEnum(Role);

export const UserStatus = { ACTIVE: 'ACTIVE', BANNED: 'BANNED', DELETED: 'DELETED' } as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];
export const UserStatusSchema = z.nativeEnum(UserStatus);

export const Composition = { MEN: 'MEN', MIXED: 'MIXED' } as const;
export type Composition = (typeof Composition)[keyof typeof Composition];
export const CompositionSchema = z.nativeEnum(Composition);

export const SquadStatus = {
  FORMING: 'FORMING',
  SALE: 'SALE',
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  DISBANDED: 'DISBANDED',
} as const;
export type SquadStatus = (typeof SquadStatus)[keyof typeof SquadStatus];
export const SquadStatusSchema = z.nativeEnum(SquadStatus);

export const SeasonStatus = {
  DRAFT: 'DRAFT',
  SALE: 'SALE',
  ACTIVE: 'ACTIVE',
  FINISHED: 'FINISHED',
  CANCELLED: 'CANCELLED',
} as const;
export type SeasonStatus = (typeof SeasonStatus)[keyof typeof SeasonStatus];
export const SeasonStatusSchema = z.nativeEnum(SeasonStatus);

export const GatheringStatus = {
  PLANNED: 'PLANNED',
  CONFIRMED: 'CONFIRMED',
  DONE: 'DONE',
  CANCELLED: 'CANCELLED',
} as const;
export type GatheringStatus = (typeof GatheringStatus)[keyof typeof GatheringStatus];
export const GatheringStatusSchema = z.nativeEnum(GatheringStatus);

export const Rsvp = { YES: 'YES', NO: 'NO', MAYBE: 'MAYBE' } as const;
export type Rsvp = (typeof Rsvp)[keyof typeof Rsvp];
export const RsvpSchema = z.nativeEnum(Rsvp);

export const MemberStatus = {
  ACTIVE: 'ACTIVE',
  LEFT: 'LEFT',
  REMOVED: 'REMOVED',
  SUBSTITUTE: 'SUBSTITUTE',
} as const;
export type MemberStatus = (typeof MemberStatus)[keyof typeof MemberStatus];
export const MemberStatusSchema = z.nativeEnum(MemberStatus);

export const OrderKind = {
  SEASON: 'SEASON',
  TRIAL: 'TRIAL',
  SUBSTITUTE: 'SUBSTITUTE',
  EXTRA: 'EXTRA',
  CORPORATE: 'CORPORATE',
  /** Заявка из нескольких разовых мест с одной оплатой (Stage 17, ADR-35): родитель позиций */
  BUNDLE: 'BUNDLE',
  /** Разовое место на один сбор чужого отряда (Stage 17, ADR-35) */
  SINGLE: 'SINGLE',
} as const;
export type OrderKind = (typeof OrderKind)[keyof typeof OrderKind];
export const OrderKindSchema = z.nativeEnum(OrderKind);

export const OrderStatus = {
  PENDING: 'PENDING',
  PAID: 'PAID',
  REFUNDED: 'REFUNDED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
} as const;
export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];
export const OrderStatusSchema = z.nativeEnum(OrderStatus);

export const PaymentProvider = {
  MANUAL: 'MANUAL',
  TELEGRAM: 'TELEGRAM',
  ROBOKASSA: 'ROBOKASSA',
  STARS: 'STARS',
} as const;
export type PaymentProvider = (typeof PaymentProvider)[keyof typeof PaymentProvider];
export const PaymentProviderSchema = z.nativeEnum(PaymentProvider);

export const PoolStatus = {
  WAITING: 'WAITING',
  OFFERED: 'OFFERED',
  PLACED: 'PLACED',
  WITHDRAWN: 'WITHDRAWN',
} as const;
export type PoolStatus = (typeof PoolStatus)[keyof typeof PoolStatus];
export const PoolStatusSchema = z.nativeEnum(PoolStatus);

export const FlagKind = {
  NO_SHOW: 'NO_SHOW',
  LATE: 'LATE',
  TOXIC: 'TOXIC',
  LEVEL_MISMATCH: 'LEVEL_MISMATCH',
  OTHER: 'OTHER',
} as const;
export type FlagKind = (typeof FlagKind)[keyof typeof FlagKind];
export const FlagKindSchema = z.nativeEnum(FlagKind);

export const NotificationKind = {
  SQUAD_FORMED: 'SQUAD_FORMED',
  SALE_OPEN: 'SALE_OPEN',
  PAY_DEADLINE: 'PAY_DEADLINE',
  RSVP_REQUEST: 'RSVP_REQUEST',
  DETAILS_24H: 'DETAILS_24H',
  DETAILS_3H: 'DETAILS_3H',
  FEEDBACK: 'FEEDBACK',
  RENEWAL: 'RENEWAL',
  SEAT_OFFER: 'SEAT_OFFER',
  /** Важное изменение уже назначенного сбора: перенос, отмена, отказ площадки */
  GATHERING_CHANGED: 'GATHERING_CHANGED',
  CUSTOM: 'CUSTOM',
} as const;
export type NotificationKind = (typeof NotificationKind)[keyof typeof NotificationKind];
export const NotificationKindSchema = z.nativeEnum(NotificationKind);

export const ActivitySource = { CATALOG: 'CATALOG', PROPOSED: 'PROPOSED' } as const;
export type ActivitySource = (typeof ActivitySource)[keyof typeof ActivitySource];
export const ActivitySourceSchema = z.nativeEnum(ActivitySource);

export const ProposalStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  MERGED: 'MERGED',
  REJECTED: 'REJECTED',
} as const;
export type ProposalStatus = (typeof ProposalStatus)[keyof typeof ProposalStatus];
export const ProposalStatusSchema = z.nativeEnum(ProposalStatus);

export const OpenSeatStatus = {
  OPEN: 'OPEN',
  OFFERED: 'OFFERED',
  FILLED: 'FILLED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
} as const;
export type OpenSeatStatus = (typeof OpenSeatStatus)[keyof typeof OpenSeatStatus];
export const OpenSeatStatusSchema = z.nativeEnum(OpenSeatStatus);

/**
 * Что продаётся открытым местом (Stage 11): сезон в отряде, место запасного, замена на сбор;
 * Stage 17 — разовое место на один сбор (строка заводится в момент брони).
 */
export const OpenSeatKind = {
  SEASON: 'SEASON',
  RESERVE: 'RESERVE',
  SUBSTITUTE: 'SUBSTITUTE',
  SINGLE: 'SINGLE',
} as const;
export type OpenSeatKind = (typeof OpenSeatKind)[keyof typeof OpenSeatKind];
export const OpenSeatKindSchema = z.nativeEnum(OpenSeatKind);

/**
 * Формат турнира (Stage 17, ADR-35). Пусто у турнира = `STANDARD` (участвовать до конца);
 * `KNOCKOUT` — на выбывание, выбывший может уйти. Одинаков у сбора и его близнеца.
 */
export const TournamentFormat = { STANDARD: 'STANDARD', KNOCKOUT: 'KNOCKOUT' } as const;
export type TournamentFormat = (typeof TournamentFormat)[keyof typeof TournamentFormat];
export const TournamentFormatSchema = z.nativeEnum(TournamentFormat);

/**
 * По какому праву человек на сборе (`Attendance.kind`, Stage 17): состав отряда, замена, пробный,
 * разовое место. Явка состава и разовых считается раздельно.
 */
export const AttendanceKind = {
  SQUAD: 'SQUAD',
  SUBSTITUTE: 'SUBSTITUTE',
  TRIAL: 'TRIAL',
  SINGLE: 'SINGLE',
} as const;
export type AttendanceKind = (typeof AttendanceKind)[keyof typeof AttendanceKind];
export const AttendanceKindSchema = z.nativeEnum(AttendanceKind);

export const SquadPollKind = { NAME: 'NAME', EXTRA: 'EXTRA' } as const;
export type SquadPollKind = (typeof SquadPollKind)[keyof typeof SquadPollKind];
export const SquadPollKindSchema = z.nativeEnum(SquadPollKind);

export const SquadPollStatus = { OPEN: 'OPEN', CLOSED: 'CLOSED', CANCELLED: 'CANCELLED' } as const;
export type SquadPollStatus = (typeof SquadPollStatus)[keyof typeof SquadPollStatus];
export const SquadPollStatusSchema = z.nativeEnum(SquadPollStatus);

/** Бронь слота у площадки (Stage 14, задача 2): портал площадки подтверждает или отклоняет. */
export const GatheringVenueStatus = {
  REQUESTED: 'REQUESTED',
  CONFIRMED: 'CONFIRMED',
  DECLINED: 'DECLINED',
} as const;
export type GatheringVenueStatus = (typeof GatheringVenueStatus)[keyof typeof GatheringVenueStatus];
export const GatheringVenueStatusSchema = z.nativeEnum(GatheringVenueStatus);

export const MatchProposalStatus = {
  PROPOSED: 'PROPOSED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const;
export type MatchProposalStatus = (typeof MatchProposalStatus)[keyof typeof MatchProposalStatus];
export const MatchProposalStatusSchema = z.nativeEnum(MatchProposalStatus);

/** День недели ISO: 1 = понедельник … 7 = воскресенье. */
export const WeekdaySchema = z.number().int().min(1).max(7);
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Уровень 1–5 (самооценка и скорректированный). */
export const LevelSchema = z.number().int().min(1).max(5);
export type Level = 1 | 2 | 3 | 4 | 5;
